import { useState, useEffect, useRef, FormEvent, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import {
  ShieldCheck, Camera, Wrench, FileCheck, Lightbulb,
  Check, Phone, Star, ArrowLeft, ArrowRight, Lock, Eye, FileText, TrendingUp,
  MessageCircle, MapPin, BadgeCheck, Globe,
} from "lucide-react";
import "@/styles/olaya.css";

/* -------- Contact numbers --------
 * WhatsApp: +966 54 527 4971
 * Call:     +966 54 527 5614
 */
const WA_NUMBER = "966545274971";
const WA_DISPLAY_AR = "+٩٦٦ ٥٤ ٥٢٧ ٤٩٧١";
const WA_DISPLAY_EN = "+966 54 527 4971";
const TEL = "+966545275614";
const TEL_DISPLAY_AR = "+٩٦٦ ٥٤ ٥٢٧ ٥٦١٤";
const TEL_DISPLAY_EN = "+966 54 527 5614";
const LOGO = "https://cdn.salla.sa/VjKgd/VHw8FksHL3Sc7KHQLd6HGfqin2O80HVanX3TCvDz.png";

type Lang = "ar" | "en";

/* ----------------------------- i18n ----------------------------- */
const dict = {
  ar: {
    metaTitle: "كاميرات مراقبة معتمدة وشهادة استيفاء | ضي العليا لتقنية المعلومات",
    metaDesc:
      "توريد وتركيب كاميرات Hikvision معتمدة وإصدار شهادة الاستيفاء بإجراءات سريعة ومطابقة لاشتراطات الأمن العام. احجز معاينة مجانية الآن.",
    ogTitle: "كاميرات مراقبة معتمدة وشهادة استيفاء | ضي العليا",
    ogDesc: "توريد وتركيب كاميرات Hikvision معتمدة وإصدار شهادة الاستيفاء.",
    brandName: "ضي العليا لتقنية المعلومات",
    brandSub: "Olaya Brightness",
    langToggle: "EN",
    waCta: "واتساب",
    waAria: "تواصل واتساب",
    heroPill: "شريك معتمد لكاميرات Hikvision — متوافق مع اشتراطات الأمن العام",
    h1a: "كاميرات مراقبة معتمدة",
    h1b: "+ شهادة استيفاء",
    h1c: "لمنشأتك",
    heroLead: (
      <>
        في <strong>ضي العليا لتقنية المعلومات</strong> نوفّر لك توريد وتركيب الكاميرات المعتمدة وإصدار شهادة الاستيفاء
        بإجراءات سريعة ومطابقة للأنظمة — لتحمي منشأتك وتضمن امتثالك الكامل.
      </>
    ),
    ctaBookWA: "احجز معاينة مجانية الآن",
    ctaCallDirect: "اتصل بنا مباشرة",
    freeConsult: "المعاينة والاستشارة مجانية — بدون أي التزام",
    marquee: [
      "Hikvision",
      "سجل تجاري 1010493895",
      "موثوق منصة الأعمال SBC 0000135007",
      "عقود صيانة",
      "دعم فني سريع",
    ],
    trustEyebrow: "أرقام تعكس ثقتك",
    trustTitleA: "موثوقية مثبتة بـ",
    trustTitleB: "أرقام حقيقية",
    trustStats: [
      { label: "طلب مكتمل" },
      { label: "رضا العملاء" },
      { label: "زائر يومياً" },
    ],
    hikvisionLabel: "أجهزة أصلية معتمدة",
    trustChipSBC: "موثوق منصة الأعمال",
    trustChipCR: "سجل تجاري",
    trustChipCompliant: "مطابق لاشتراطات الأمن العام",
    problemTitleA: "تركيب الكاميرات وإصدار",
    problemTitleB: "شهادة الاستيفاء",
    problemTitleC: "لم يعد خياراً",
    problemBody:
      "مع التطورات التنظيمية الحديثة في المملكة، أصبح تركيب كاميرات المراقبة المعتمدة وإصدار شهادة الاستيفاء من المتطلبات الأساسية لدى الأمن العام لكثير من الأنشطة التجارية والمستودعات والمعارض والشركات والمرافق — لضمان الامتثال للأنظمة الأمنية المعتمدة.",
    facilityChips: ["الشركات", "المعارض", "المستودعات", "المحلات التجارية", "المرافق"],
    servicesEyebrow: "خدماتنا",
    servicesTitleA: "حلول متكاملة من",
    servicesTitleB: "ضي العليا",
    servicesLead:
      "منظومة خدمات واحدة من المعاينة إلى الشهادة والصيانة — تنفيذ احترافي ومطابقة كاملة لاشتراطات الأمن العام.",
    services: [
      { t: "توريد وتركيب كاميرات Hikvision معتمدة", d: "كاميرات أصلية مع تركيب احترافي مطابق لاشتراطات الأمن العام." },
      { t: "إصدار شهادة الاستيفاء", d: "متابعة كاملة لإصدار الشهادة بإجراءات سريعة وموثقة." },
      { t: "عقود صيانة دورية", d: "خطط صيانة سنوية تضمن استمرارية النظام بكفاءة." },
      { t: "تجهيز وفق الاشتراطات المعتمدة", d: "تصميم وتنفيذ بنية مطابقة للأنظمة الأمنية." },
      { t: "حلول مخصصة لنشاطك", d: "دراسة احتياج المنشأة واقتراح الحل الأمثل." },
    ],
    serviceTags: ["داخلية", "خارجية", "ليلية", "IP / PoE", "تخزين NVR"],
    requestOfferMsg: "أهلاً، أرغب بطلب عرض مخصص لتركيب الكاميرات وشهادة الاستيفاء",
    requestOfferBtn: "اطلب عرضك المخصص",
    whyUsTitleA: "ليش تختار",
    whyUsTitleB: "ضي العليا",
    whyUsTitleC: "لتقنية المعلومات؟",
    whyUs: [
      "خبرة في أنظمة الكاميرات والشبكات والحلول الأمنية",
      "تنفيذ احترافي متوافق مع متطلبات الجهات التنظيمية",
      "سرعة في التنفيذ والدعم الفني",
      "عقود صيانة تضمن استمرارية النظام بكفاءة عالية",
      "حلول مناسبة للشركات والمعارض والمستودعات والمحلات",
    ],
    benefitsTitleA: "أهمية الكاميرات",
    benefitsTitleB: "تتجاوز",
    benefitsTitleC: "الامتثال التنظيمي",
    benefits: [
      "حماية المنشأة والأصول",
      "رفع مستوى الأمان والمراقبة",
      "توثيق العمليات والأحداث",
      "تعزيز موثوقية النشاط التجاري",
    ],
    processTitleA: "كيف",
    processTitleB: "نخدمك",
    processTitleC: "؟ خطوات بسيطة",
    steps: [
      "تواصل معنا واحجز معاينتك المجانية",
      "نعاين الموقع ونحدد الاحتياج الأمثل",
      "نوفّر ونركّب الأنظمة المعتمدة باحترافية",
      "نساعدك في إصدار شهادة الاستيفاء + الصيانة الدورية",
    ],
    formTitleA: "احصل على",
    formTitleB: "استشارة ومعاينة مجانية",
    formTitleC: "لموقعك",
    labelName: "الاسم",
    phName: "الاسم الكامل",
    labelPhone: "رقم الجوال",
    phPhone: "05XXXXXXXX",
    labelType: "نوع المنشأة",
    labelCity: "المدينة",
    phCity: "مثال: الرياض",
    facilityTypes: ["شركة", "معرض", "مستودع", "محل تجاري", "مرفق"],
    errName: "الرجاء إدخال الاسم",
    errPhone: "رقم الجوال غير صحيح",
    errCity: "الرجاء إدخال المدينة",
    submitWA: "إرسال الطلب عبر واتساب",
    waFormMsg: (name: string, phone: string, type: string, city: string) =>
      `طلب معاينة - الاسم: ${name} - الجوال: ${phone} - المنشأة: ${type} - المدينة: ${city}`,
    testimonialsTitleA: "آراء",
    testimonialsTitleB: "عملائنا",
    testimonials: [
      { name: "فيصل تركي", city: "الرياض", text: "متجر ممتاز جدًا، تعاملهم راقي وخدمة العملاء محترمة، والتنفيذ احترافي. أنصح بالتعامل معهم." },
      { name: "محمد فهد", city: "الرياض", text: "تجربة ممتازة جدًا، سرعة في التنفيذ والاحترافية واضحة." },
      { name: "Nora Alotaibi", city: "البدائع", text: "متجر يستاهل الثقة، تعامل صادق واحترافي." },
    ],
    finalTitle: "جاهز تحمي منشأتك وتضمن امتثالك؟",
    finalBody: "احجز معاينتك المجانية الآن، وفريق ضي العليا يقدّم لك الحل الأنسب حسب احتياج منشأتك.",
    finalCta: "احجز معاينة مجانية عبر واتساب",
    footerContact: "التواصل",
    footerWA: "واتساب مباشر",
    footerOfficial: "البيانات الرسمية",
    footerCR: "السجل التجاري:",
    footerVAT: "الرقم الضريبي:",
    footerSBC: "موثوق لدى منصة الأعمال — SBC 0000135007",
    footerRights: "© 2026 ضي العليا لتقنية المعلومات — جميع الحقوق محفوظة.",
    mCtaCall: "اتصل الآن",
    waDefault:
      "السلام عليكم، أرغب بحجز معاينة مجانية لتركيب كاميرات مراقبة معتمدة وإصدار شهادة الاستيفاء لمنشأتي.",
  },
  en: {
    metaTitle: "Certified CCTV & Compliance Certificate | Olaya Brightness IT",
    metaDesc:
      "Supply and installation of certified Hikvision CCTV cameras and issuance of the Compliance Certificate — fast, compliant with General Security requirements. Book your free site inspection now.",
    ogTitle: "Certified CCTV & Compliance Certificate | Olaya Brightness",
    ogDesc: "Certified Hikvision CCTV installation and Compliance Certificate issuance.",
    brandName: "Olaya Brightness IT",
    brandSub: "ضي العليا لتقنية المعلومات",
    langToggle: "AR",
    waCta: "WhatsApp",
    waAria: "Chat on WhatsApp",
    heroPill: "Authorized Hikvision partner — compliant with General Security requirements",
    h1a: "Certified CCTV cameras",
    h1b: "+ Compliance Certificate",
    h1c: "for your facility",
    heroLead: (
      <>
        At <strong>Olaya Brightness IT</strong> we supply and install certified cameras and issue the
        Compliance Certificate with fast, compliant procedures — protecting your facility and ensuring full regulatory compliance.
      </>
    ),
    ctaBookWA: "Book a free inspection now",
    ctaCallDirect: "Call us directly",
    freeConsult: "Inspection and consultation are free — with no obligation",
    marquee: [
      "Hikvision",
      "Commercial Registration 1010493895",
      "Verified on Saudi Business Center — SBC 0000135007",
      "Maintenance contracts",
      "Fast technical support",
    ],
    trustEyebrow: "Numbers that reflect your trust",
    trustTitleA: "Proven reliability with",
    trustTitleB: "real numbers",
    trustStats: [
      { label: "Completed orders" },
      { label: "Customer satisfaction" },
      { label: "Daily visitors" },
    ],
    hikvisionLabel: "Original certified equipment",
    trustChipSBC: "Verified on Saudi Business Center",
    trustChipCR: "Commercial Registration",
    trustChipCompliant: "Compliant with General Security requirements",
    problemTitleA: "Installing cameras and issuing the",
    problemTitleB: "Compliance Certificate",
    problemTitleC: "is no longer optional",
    problemBody:
      "With the latest regulatory developments in the Kingdom, installing certified surveillance cameras and issuing the Compliance Certificate has become a core requirement from General Security for many businesses, warehouses, showrooms, companies, and facilities — to ensure compliance with the approved security regulations.",
    facilityChips: ["Companies", "Showrooms", "Warehouses", "Retail shops", "Facilities"],
    servicesEyebrow: "Our Services",
    servicesTitleA: "Integrated solutions by",
    servicesTitleB: "Olaya Brightness",
    servicesLead:
      "One end-to-end ecosystem — from inspection to certification and maintenance — with professional execution and full compliance with General Security requirements.",
    services: [
      { t: "Supply & installation of certified Hikvision cameras", d: "Original cameras with professional installation compliant with General Security requirements." },
      { t: "Compliance Certificate issuance", d: "Full follow-up to issue the certificate through fast, documented procedures." },
      { t: "Periodic maintenance contracts", d: "Annual maintenance plans that keep your system running efficiently." },
      { t: "Setup per approved requirements", d: "Design and execution of infrastructure that matches the security regulations." },
      { t: "Custom solutions for your business", d: "We study your facility's needs and propose the optimal solution." },
    ],
    serviceTags: ["Indoor", "Outdoor", "Night vision", "IP / PoE", "NVR storage"],
    requestOfferMsg: "Hello, I'd like a custom quote for camera installation and the Compliance Certificate",
    requestOfferBtn: "Request your custom quote",
    whyUsTitleA: "Why choose",
    whyUsTitleB: "Olaya Brightness",
    whyUsTitleC: "IT?",
    whyUs: [
      "Expertise in camera systems, networking, and security solutions",
      "Professional execution aligned with regulator requirements",
      "Fast execution and technical support",
      "Maintenance contracts that ensure high system uptime",
      "Solutions tailored for companies, showrooms, warehouses, and shops",
    ],
    benefitsTitleA: "The value of cameras",
    benefitsTitleB: "goes beyond",
    benefitsTitleC: "regulatory compliance",
    benefits: [
      "Protect your facility and assets",
      "Raise security and monitoring standards",
      "Document operations and incidents",
      "Reinforce your business's credibility",
    ],
    processTitleA: "How do we",
    processTitleB: "serve you",
    processTitleC: "? A few simple steps",
    steps: [
      "Contact us and book your free inspection",
      "We inspect the site and define the optimal need",
      "We supply and install certified systems professionally",
      "We help you issue the Compliance Certificate + ongoing maintenance",
    ],
    formTitleA: "Get a",
    formTitleB: "free consultation & inspection",
    formTitleC: "for your site",
    labelName: "Name",
    phName: "Full name",
    labelPhone: "Mobile number",
    phPhone: "05XXXXXXXX",
    labelType: "Facility type",
    labelCity: "City",
    phCity: "e.g. Riyadh",
    facilityTypes: ["Company", "Showroom", "Warehouse", "Retail shop", "Facility"],
    errName: "Please enter your name",
    errPhone: "Invalid mobile number",
    errCity: "Please enter your city",
    submitWA: "Send request via WhatsApp",
    waFormMsg: (name: string, phone: string, type: string, city: string) =>
      `Inspection request — Name: ${name} — Mobile: ${phone} — Facility: ${type} — City: ${city}`,
    testimonialsTitleA: "What our",
    testimonialsTitleB: "clients say",
    testimonials: [
      { name: "Faisal Turki", city: "Riyadh", text: "Excellent store — their dealings are top-notch and customer service is respectful, with professional execution. Highly recommended." },
      { name: "Mohammed Fahad", city: "Riyadh", text: "A great experience — speed of execution and professionalism are very clear." },
      { name: "Nora Alotaibi", city: "Al-Badayea", text: "A trustworthy store — honest and professional dealings." },
    ],
    finalTitle: "Ready to protect your facility and secure your compliance?",
    finalBody: "Book your free inspection now — the Olaya Brightness team will propose the best fit for your facility.",
    finalCta: "Book a free inspection on WhatsApp",
    footerContact: "Contact",
    footerWA: "WhatsApp direct",
    footerOfficial: "Official details",
    footerCR: "Commercial Registration:",
    footerVAT: "VAT Number:",
    footerSBC: "Verified on Saudi Business Center — SBC 0000135007",
    footerRights: "© 2026 Olaya Brightness IT — All rights reserved.",
    mCtaCall: "Call now",
    waDefault:
      "Hello, I'd like to book a free inspection for installing certified surveillance cameras and issuing the Compliance Certificate for my facility.",
  },
} as const;

/* ----------------------------- helpers ----------------------------- */

function WAIcon({ className = "h-5 w-5" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.711.306 1.265.489 1.697.625.713.227 1.362.195 1.875.118.572-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
    </svg>
  );
}

/** Count-up driven by IntersectionObserver — GPU-only, runs once. */
function CountUp({ to, prefix = "", suffix = "", duration = 1400, label, variant = "light", lang }: {
  to: number; prefix?: string; suffix?: string; duration?: number; label: string;
  variant?: "light" | "dark"; lang: Lang;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [val, setVal] = useState(0);
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (done || !ref.current) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setVal(to); setDone(true); return;
    }
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setDone(true);
        const start = performance.now();
        const tick = (now: number) => {
          const p = Math.min(1, (now - start) / duration);
          const eased = 1 - Math.pow(1 - p, 3);
          setVal(Math.round(to * eased));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
        io.disconnect();
      }
    }, { threshold: 0.3 });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [to, duration, done]);
  const numberColor = variant === "dark" ? "#fff" : "var(--ol-primary)";
  const labelClass = variant === "dark" ? "text-white/75" : "text-[color:var(--ol-text-muted)]";
  return (
    <div ref={ref} className="text-center">
      <div
        className="text-4xl md:text-5xl lg:text-6xl font-black tabular-nums leading-none tracking-tight"
        style={{ color: numberColor, textShadow: variant === "dark" ? "0 2px 30px rgba(201,162,39,0.25)" : "none" }}
      >
        {prefix}{val.toLocaleString(lang === "ar" ? "ar-EG" : "en-US")}{suffix}
      </div>
      <div className={`text-sm md:text-base font-medium mt-2 ${labelClass}`}>{label}</div>
    </div>
  );
}

/* ----------------------------- page ----------------------------- */

export default function Olayabrightness() {
  const [lang, setLang] = useState<Lang>(() => {
    if (typeof window === "undefined") return "ar";
    return (window.localStorage.getItem("olaya-lang") as Lang) || "ar";
  });
  const t = dict[lang];
  const isRTL = lang === "ar";
  const dir: "rtl" | "ltr" = isRTL ? "rtl" : "ltr";
  const Arrow = isRTL ? ArrowLeft : ArrowRight;

  const waLink = useMemo(
    () => (msg: string = t.waDefault) =>
      `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(msg)}`,
    [t.waDefault]
  );

  const facilityTypes: readonly string[] = t.facilityTypes;
  const [form, setForm] = useState<{ name: string; phone: string; type: string; city: string }>({
    name: "", phone: "", type: facilityTypes[0], city: "",
  });
  const [err, setErr] = useState("");

  // Sync facility select if language switches
  useEffect(() => {
    setForm((f) => ({ ...f, type: facilityTypes[0] }));
  }, [lang, facilityTypes]);

  // Persist language + set html lang/dir globally so it flows to fonts too
  useEffect(() => {
    window.localStorage.setItem("olaya-lang", lang);
  }, [lang]);

  // Add bottom padding on mobile when fixed CTA bar is visible
  useEffect(() => {
    document.body.classList.add("olaya-active");
    return () => document.body.classList.remove("olaya-active");
  }, []);

  // Smooth anchor scroll with View Transitions where supported
  const smoothNav = (e: React.MouseEvent<HTMLAnchorElement>, hash: string) => {
    const el = document.querySelector(hash);
    if (!el) return;
    e.preventDefault();
    const go = () => el.scrollIntoView({ behavior: "smooth", block: "start" });
    type DocVT = Document & { startViewTransition?: (cb: () => void) => void };
    const doc = document as DocVT;
    if (typeof doc.startViewTransition === "function") {
      doc.startViewTransition(go);
    } else {
      go();
    }
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setErr("");
    const phoneClean = form.phone.replace(/\D/g, "");
    if (!form.name.trim()) return setErr(t.errName);
    if (phoneClean.length < 9) return setErr(t.errPhone);
    if (!form.city.trim()) return setErr(t.errCity);
    const msg = t.waFormMsg(form.name.trim(), form.phone.trim(), form.type, form.city.trim());
    window.open(waLink(msg), "_blank", "noopener,noreferrer");
  };

  const jsonLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      name: lang === "ar" ? "ضي العليا لتقنية المعلومات" : "Olaya Brightness IT",
      alternateName: lang === "ar" ? "Olaya Brightness" : "ضي العليا لتقنية المعلومات",
      image: LOGO,
      telephone: TEL,
      url: "https://itqan.agency/olayabrightness",
      address: { "@type": "PostalAddress", addressCountry: "SA" },
      areaServed: "SA",
      description: t.metaDesc,
      makesOffer: {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name:
            lang === "ar"
              ? "تركيب كاميرات مراقبة معتمدة وإصدار شهادة الاستيفاء"
              : "Certified CCTV installation and Compliance Certificate issuance",
        },
      },
      contactPoint: [
        { "@type": "ContactPoint", telephone: TEL, contactType: "customer service", areaServed: "SA" },
        { "@type": "ContactPoint", telephone: `+${WA_NUMBER}`, contactType: "sales", areaServed: "SA" },
      ],
    }),
    [lang, t.metaDesc]
  );

  return (
    <div
      className={`olaya-root min-h-screen overflow-x-hidden ${lang === "en" ? "olaya-ltr" : ""}`}
      dir={dir}
      lang={lang}
    >
      <Helmet>
        <html lang={lang} dir={dir} />
        <title>{t.metaTitle}</title>
        <meta name="description" content={t.metaDesc} />
        <meta name="theme-color" content="#2b3076" />
        <link rel="canonical" href="https://itqan.agency/olayabrightness" />
        <link rel="alternate" hrefLang="ar-SA" href="https://itqan.agency/olayabrightness" />
        <link rel="alternate" hrefLang="en" href="https://itqan.agency/olayabrightness?lang=en" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        <link rel="preload" as="image" href={LOGO} fetchPriority="high" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={t.ogTitle} />
        <meta property="og:description" content={t.ogDesc} />
        <meta property="og:image" content={LOGO} />
        <meta property="og:url" content="https://itqan.agency/olayabrightness" />
        <meta property="og:locale" content={lang === "ar" ? "ar_SA" : "en_US"} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* ===================== HEADER ===================== */}
      <header
        className="sticky top-0 z-40 border-b ol-glass"
        style={{ borderColor: "var(--ol-border)" }}
      >
        <div className="container mx-auto px-4 h-[68px] md:h-20 flex items-center justify-between gap-3">
          <a href="#top" onClick={(e) => smoothNav(e, "#top")} className="flex items-center gap-3 min-w-0">
            <img src={LOGO} alt={t.brandName} width={48} height={48} className="h-10 md:h-12 w-auto" decoding="async" />
            <div className="hidden sm:block min-w-0">
              <div className="font-extrabold text-sm md:text-base leading-tight truncate" style={{ color: "var(--ol-primary)" }}>
                {t.brandName}
              </div>
              <div className="text-xs text-[color:var(--ol-text-muted)]">{t.brandSub}</div>
            </div>
          </a>
          <div className="flex items-center gap-2 md:gap-3">
            <button
              type="button"
              onClick={() => setLang(lang === "ar" ? "en" : "ar")}
              className="inline-flex items-center gap-1.5 h-10 md:h-11 px-3 rounded-full font-bold text-sm border transition-colors hover:bg-[color:var(--ol-primary)] hover:text-white"
              style={{ borderColor: "var(--ol-border)", color: "var(--ol-primary)", background: "white" }}
              aria-label={lang === "ar" ? "Switch to English" : "التبديل إلى العربية"}
            >
              <Globe className="h-4 w-4" />
              <span dir="ltr">{t.langToggle}</span>
            </button>
            <a href={`tel:${TEL}`} className="hidden md:inline-flex items-center gap-2 font-bold text-sm" style={{ color: "var(--ol-primary)" }}>
              <Phone className="h-4 w-4" />
              <span dir="ltr">{lang === "ar" ? TEL_DISPLAY_AR : TEL_DISPLAY_EN}</span>
            </a>
            <a
              href={waLink()}
              target="_blank"
              rel="noopener noreferrer"
              className="ol-btn ol-btn-wa"
              style={{ padding: "0.55rem 1rem", minHeight: 44, fontSize: "0.9rem" }}
            >
              <WAIcon className="h-4 w-4" /> {t.waCta}
            </a>
          </div>
        </div>
      </header>

      <main id="top">
        {/* ===================== HERO ===================== */}
        <section className="ol-section ol-hero">
          <div className="ol-aurora" aria-hidden="true" />
          <div className="ol-grain" aria-hidden="true" />

          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="ol-pill mb-6 ol-reveal" style={{ animationDelay: "40ms" }}>
                <BadgeCheck className="h-4 w-4" />
                {t.heroPill}
              </div>
              <h1 className="ol-h1 mb-5 ol-reveal" style={{ color: "var(--ol-text)", animationDelay: "120ms" }}>
                {t.h1a}{" "}
                <span style={{ color: "var(--ol-primary)" }}>{t.h1b}</span>{" "}
                {t.h1c}
              </h1>
              <p className="ol-lead mb-8 max-w-3xl mx-auto ol-reveal" style={{ animationDelay: "200ms" }}>
                {t.heroLead}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center ol-reveal" style={{ animationDelay: "280ms" }}>
                <a href={waLink()} target="_blank" rel="noopener noreferrer" className="ol-btn ol-btn-wa">
                  <WAIcon /> {t.ctaBookWA}
                </a>
                <a href={`tel:${TEL}`} className="ol-btn ol-btn-outline">
                  <Phone className="h-5 w-5" /> {t.ctaCallDirect}
                </a>
              </div>
              <p className="mt-5 text-sm text-[color:var(--ol-text-muted)] flex items-center justify-center gap-2 ol-reveal" style={{ animationDelay: "360ms" }}>
                <Check className="h-4 w-4" style={{ color: "var(--ol-wa)" }} />
                {t.freeConsult}
              </p>
            </div>
          </div>
        </section>

        {/* ===================== MARQUEE ===================== */}
        <div className="py-4 border-y" style={{ background: "white", borderColor: "var(--ol-border)" }} aria-hidden="true">
          <div className="ol-marquee">
            <div className="ol-marquee-track">
              {Array.from({ length: 2 }).map((_, k) => (
                <div key={k} className="flex gap-12 px-6">
                  {t.marquee.map((m, i) => (
                    <span key={i} className="ol-marquee-item"><span className="ol-marquee-dot" /> {m}</span>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ===================== TRUST BAND ===================== */}
        <section className="ol-trust-band ol-trust-band--v2 py-16 md:py-24" aria-label="trust stats">
          <div className="ol-trust-pattern" aria-hidden="true" />
          <div className="container mx-auto px-4 relative">
            <div className="text-center mb-10 md:mb-14 ol-reveal">
              <span className="ol-trust-eyebrow">
                <span className="ol-trust-dot" />
                {t.trustEyebrow}
              </span>
              <h2 className="text-white font-extrabold mt-4 text-2xl md:text-3xl lg:text-4xl tracking-tight">
                {t.trustTitleA} <span className="ol-gold-text">{t.trustTitleB}</span>
              </h2>
            </div>

            <div className="ol-trust-grid">
              <div className="ol-trust-cell ol-reveal">
                <CountUp to={2200} prefix="+" label={t.trustStats[0].label} variant="dark" lang={lang} />
              </div>
              <div className="ol-trust-cell ol-reveal" style={{ animationDelay: "80ms" }}>
                <CountUp to={95} suffix="%" label={t.trustStats[1].label} variant="dark" lang={lang} />
              </div>
              <div className="ol-trust-cell ol-reveal" style={{ animationDelay: "160ms" }}>
                <CountUp to={25000} prefix="+" label={t.trustStats[2].label} variant="dark" lang={lang} />
              </div>
              <div className="ol-trust-cell ol-reveal text-center" style={{ animationDelay: "240ms" }}>
                <div className="text-3xl md:text-4xl lg:text-5xl font-black text-white leading-none tracking-tight">
                  Hikvision
                </div>
                <div className="text-sm md:text-base text-white/75 font-medium mt-2">{t.hikvisionLabel}</div>
              </div>
            </div>

            <div className="mt-12 md:mt-16 flex flex-wrap items-center justify-center gap-3 md:gap-4 ol-reveal" style={{ animationDelay: "320ms" }}>
              <span className="ol-trust-chip">
                <ShieldCheck className="h-4 w-4" />
                {t.trustChipSBC}
                <span dir="ltr" className="font-bold text-white/95">SBC 0000135007</span>
              </span>
              <span className="ol-trust-chip">
                <BadgeCheck className="h-4 w-4" />
                {t.trustChipCR}
                <span dir="ltr" className="font-bold text-white/95">1010493895</span>
              </span>
              <span className="ol-trust-chip">
                <FileCheck className="h-4 w-4" />
                {t.trustChipCompliant}
              </span>
            </div>
          </div>
        </section>

        {/* ===================== PROBLEM / URGENCY ===================== */}
        <section className="ol-section">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center ol-reveal">
              <h2 className="ol-h2 mb-6">
                {t.problemTitleA}{" "}
                <span style={{ color: "var(--ol-primary)" }}>{t.problemTitleB}</span> {t.problemTitleC}
              </h2>
              <p className="ol-lead mb-8">{t.problemBody}</p>
              <div className="flex flex-wrap justify-center gap-2">
                {t.facilityChips.map((c) => (
                  <span
                    key={c}
                    className="px-4 py-2 rounded-full text-sm font-bold"
                    style={{
                      border: "1.5px solid color-mix(in oklch, var(--ol-primary) 30%, white)",
                      color: "var(--ol-primary)",
                      background: "color-mix(in oklch, var(--ol-primary) 5%, white)",
                    }}
                  >
                    {c}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ===================== SERVICES (BENTO v2) ===================== */}
        <section id="services" className="ol-section" style={{ background: "var(--ol-bg-tint)" }}>
          <div className="container mx-auto px-4">
            <div className="text-center mb-14 ol-reveal max-w-2xl mx-auto">
              <span className="ol-svc-eyebrow">
                <span className="ol-svc-eyebrow-dot" />
                {t.servicesEyebrow}
              </span>
              <h2 className="ol-h2 mt-4">
                {t.servicesTitleA}{" "}
                <span style={{ color: "var(--ol-primary)" }}>{t.servicesTitleB}</span>
              </h2>
              <p className="ol-lead mt-4">{t.servicesLead}</p>
            </div>

            <div className="ol-bento max-w-6xl mx-auto">
              {/* HERO TILE — Hikvision */}
              <article className="ol-svc-hero ol-bento-a ol-reveal">
                <div className="flex flex-col h-full">
                  <div className="ol-svc-hero-icon mb-6">
                    <Camera className="h-8 w-8" />
                  </div>
                  <h3 className="text-2xl md:text-3xl font-extrabold leading-snug mb-3">
                    {t.services[0].t}
                  </h3>
                  <p className="text-base md:text-lg leading-relaxed max-w-lg">
                    {t.services[0].d}
                  </p>
                  <div className="mt-auto pt-8 flex flex-wrap gap-2">
                    {t.serviceTags.map((tag) => (
                      <span key={tag} className="ol-svc-hero-tag">{tag}</span>
                    ))}
                  </div>
                </div>
              </article>

              {/* Certificate */}
              <article className="ol-svc-tile ol-svc-tile--gold ol-bento-b ol-reveal" style={{ animationDelay: "60ms" }}>
                <div className="flex items-start gap-4">
                  <div className="ol-svc-icon ol-svc-icon--gold flex-shrink-0">
                    <FileCheck className="h-6 w-6" />
                  </div>
                  <div className="flex-1">
                    <h3 className="mb-1.5">{t.services[1].t}</h3>
                    <p>{t.services[1].d}</p>
                  </div>
                </div>
              </article>

              {/* 3 supporting tiles */}
              {[2, 3, 4].map((i, idx) => {
                const s = t.services[i];
                const cls = ["ol-bento-c", "ol-bento-d", "ol-bento-e"][idx];
                const icons = [Wrench, ShieldCheck, Lightbulb];
                const Ico = icons[idx];
                return (
                  <article
                    key={i}
                    className={`ol-svc-tile ol-svc-tile--indigo ${cls} ol-reveal`}
                    style={{ animationDelay: `${120 + idx * 60}ms` }}
                  >
                    <div className="ol-svc-icon">
                      <Ico className="h-6 w-6" />
                    </div>
                    <h3>{s.t}</h3>
                    <p>{s.d}</p>
                  </article>
                );
              })}
            </div>

            <div className="text-center mt-14">
              <a
                href={waLink(t.requestOfferMsg)}
                target="_blank"
                rel="noopener noreferrer"
                className="ol-btn ol-btn-primary ol-btn-primary-xl"
              >
                {t.requestOfferBtn} <Arrow className="h-5 w-5" />
              </a>
              <p className="mt-4 text-sm" style={{ color: "var(--ol-text-muted)" }}>
                {t.freeConsult}
              </p>
            </div>
          </div>
        </section>

        {/* ===================== WHY US ===================== */}
        <section className="ol-section">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12 ol-reveal">
              <h2 className="ol-h2">
                {t.whyUsTitleA} <span style={{ color: "var(--ol-primary)" }}>{t.whyUsTitleB}</span> {t.whyUsTitleC}
              </h2>
            </div>
            <div className="max-w-3xl mx-auto space-y-3">
              {t.whyUs.map((p, i) => (
                <div
                  key={i}
                  className="ol-reveal flex items-start gap-4 p-4 md:p-5 rounded-2xl"
                  style={{
                    border: "1px solid var(--ol-border-tint)",
                    background: "color-mix(in oklch, white 96%, var(--ol-primary-50))",
                    animationDelay: `${i * 50}ms`,
                  }}
                >
                  <div className="h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: "var(--ol-primary)", color: "#fff" }}>
                    <Check className="h-5 w-5" />
                  </div>
                  <p className="text-base md:text-lg font-medium pt-1.5">{p}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===================== BENEFITS (BENTO) ===================== */}
        <section className="ol-section" style={{ background: "var(--ol-bg-tint)" }}>
          <div className="container mx-auto px-4">
            <div className="text-center mb-12 ol-reveal">
              <h2 className="ol-h2">
                {t.benefitsTitleA} <span style={{ color: "var(--ol-primary)" }}>{t.benefitsTitleB}</span> {t.benefitsTitleC}
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 max-w-6xl mx-auto">
              {t.benefits.map((label, i) => {
                const Ico = [Lock, Eye, FileText, TrendingUp][i];
                return (
                  <article
                    key={i}
                    className="ol-card text-center ol-reveal"
                    style={{ animationDelay: `${i * 60}ms` }}
                  >
                    <div className="h-16 w-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
                      style={{ background: "color-mix(in oklch, var(--ol-gold) 15%, white)", color: "var(--ol-gold)" }}>
                      <Ico className="h-8 w-8" />
                    </div>
                    <h3 className="text-base font-extrabold leading-snug">{label}</h3>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* ===================== PROCESS ===================== */}
        <section className="ol-section">
          <div className="container mx-auto px-4">
            <div className="text-center mb-14 ol-reveal">
              <h2 className="ol-h2">
                {t.processTitleA} <span style={{ color: "var(--ol-primary)" }}>{t.processTitleB}</span>{t.processTitleC}
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 max-w-6xl mx-auto">
              {t.steps.map((s, i) => (
                <article
                  key={i}
                  className="ol-card relative pt-12 ol-reveal"
                  style={{ animationDelay: `${i * 80}ms` }}
                >
                  <div
                    className={`absolute -top-5 ${isRTL ? "right-6" : "left-6"} h-12 w-12 rounded-full flex items-center justify-center font-black text-lg`}
                    style={{
                      background: "var(--ol-primary)",
                      color: "#fff",
                      boxShadow: "0 12px 24px -10px color-mix(in oklch, var(--ol-primary) 60%, transparent)",
                    }}
                  >
                    {i + 1}
                  </div>
                  <p className="font-bold text-base leading-relaxed">{s}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ===================== LEAD FORM ===================== */}
        <section id="lead" className="ol-section" style={{ background: "var(--ol-bg-tint)" }}>
          <div className="container mx-auto px-4">
            <div className="max-w-2xl mx-auto ol-reveal">
              <div className="text-center mb-8">
                <h2 className="ol-h2">
                  {t.formTitleA} <span style={{ color: "var(--ol-primary)" }}>{t.formTitleB}</span> {t.formTitleC}
                </h2>
              </div>
              <form onSubmit={onSubmit} className="ol-card !p-6 md:!p-8 space-y-4" noValidate>
                <div>
                  <label htmlFor="ol-name" className="block text-sm font-bold mb-2">{t.labelName}</label>
                  <input
                    id="ol-name"
                    className="ol-input"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder={t.phName}
                    maxLength={80}
                    autoComplete="name"
                  />
                </div>
                <div>
                  <label htmlFor="ol-phone" className="block text-sm font-bold mb-2">{t.labelPhone}</label>
                  <input
                    id="ol-phone"
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel"
                    className="ol-input"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder={t.phPhone}
                    maxLength={20}
                    dir="ltr"
                  />
                </div>
                <div>
                  <label htmlFor="ol-type" className="block text-sm font-bold mb-2">{t.labelType}</label>
                  <select
                    id="ol-type"
                    className="ol-input"
                    value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value })}
                  >
                    {facilityTypes.map((ft) => <option key={ft} value={ft}>{ft}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="ol-city" className="block text-sm font-bold mb-2">{t.labelCity}</label>
                  <input
                    id="ol-city"
                    className="ol-input"
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                    placeholder={t.phCity}
                    maxLength={60}
                    autoComplete="address-level2"
                  />
                </div>
                {err && <p className="text-sm font-bold text-red-600" role="alert">{err}</p>}
                <button type="submit" className="ol-btn ol-btn-wa w-full !py-4 text-base">
                  <WAIcon /> {t.submitWA}
                </button>
              </form>
            </div>
          </div>
        </section>

        {/* ===================== TESTIMONIALS ===================== */}
        <section className="ol-section">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12 ol-reveal">
              <h2 className="ol-h2">
                {t.testimonialsTitleA} <span style={{ color: "var(--ol-primary)" }}>{t.testimonialsTitleB}</span>
              </h2>
            </div>
            <div className="grid md:grid-cols-3 gap-5 max-w-6xl mx-auto">
              {t.testimonials.map((tst, i) => (
                <article key={i} className="ol-card ol-reveal" style={{ animationDelay: `${i * 80}ms` }}>
                  <div className="flex gap-1 mb-3" aria-label="5 stars">
                    {Array.from({ length: 5 }).map((_, k) => (
                      <Star key={k} className="h-4 w-4 fill-current" style={{ color: "var(--ol-gold)" }} />
                    ))}
                  </div>
                  <p className="text-[color:var(--ol-text-muted)] leading-relaxed mb-5 text-sm">"{tst.text}"</p>
                  <div className="flex items-center gap-3 pt-4 border-t" style={{ borderColor: "var(--ol-border)" }}>
                    <div className="h-11 w-11 rounded-full flex items-center justify-center font-bold text-white"
                      style={{ background: "var(--ol-primary)" }} aria-hidden="true">
                      {tst.name.charAt(0)}
                    </div>
                    <div>
                      <div className="font-bold text-sm">{tst.name}</div>
                      <div className="text-xs text-[color:var(--ol-text-muted)] flex items-center gap-1">
                        <MapPin className="h-3 w-3" /> {tst.city}
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ===================== FINAL CTA ===================== */}
        <section className="ol-trust-band py-16 md:py-24">
          <div className="container mx-auto px-4 text-center">
            <div className="max-w-3xl mx-auto ol-reveal">
              <h2 className="ol-h2 text-white mb-5">{t.finalTitle}</h2>
              <p className="ol-lead text-white/85 mb-8">{t.finalBody}</p>
              <a
                href={waLink()}
                target="_blank"
                rel="noopener noreferrer"
                className="ol-btn ol-btn-wa !text-lg !py-5 !px-8"
              >
                <WAIcon className="h-6 w-6" /> {t.finalCta}
              </a>
            </div>
          </div>
        </section>
      </main>

      {/* ===================== FOOTER ===================== */}
      <footer className="py-12 border-t" style={{ background: "#fff", borderColor: "var(--ol-border)" }}>
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div>
              <img src={LOGO} alt={t.brandName} width={48} height={48} className="h-12 w-auto mb-4" loading="lazy" decoding="async" />
              <div className="font-extrabold mb-1" style={{ color: "var(--ol-primary)" }}>{t.brandName}</div>
              <div className="text-sm text-[color:var(--ol-text-muted)] mb-3">{t.brandSub}</div>
              <a href="https://shop.olayabrightness.com/ar/" target="_blank" rel="noopener noreferrer"
                className="text-sm font-bold" style={{ color: "var(--ol-primary)" }}>
                shop.olayabrightness.com →
              </a>
            </div>
            <div className="text-sm space-y-2">
              <div className="font-bold mb-3">{t.footerContact}</div>
              <a href={`tel:${TEL}`} className="flex items-center gap-2 hover:underline">
                <Phone className="h-4 w-4" />
                <span dir="ltr">{lang === "ar" ? TEL_DISPLAY_AR : TEL_DISPLAY_EN}</span>
              </a>
              <a href={waLink()} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:underline">
                <MessageCircle className="h-4 w-4" />
                <span>{t.footerWA}</span>
                <span dir="ltr" className="text-[color:var(--ol-text-muted)]">
                  {lang === "ar" ? WA_DISPLAY_AR : WA_DISPLAY_EN}
                </span>
              </a>
            </div>
            <div className="text-sm space-y-2">
              <div className="font-bold mb-3">{t.footerOfficial}</div>
              <div>{t.footerCR} <span className="font-bold" dir="ltr">1010493895</span></div>
              <div>{t.footerVAT} <span className="font-bold" dir="ltr">310874547900003</span></div>
              <a
                href="https://eauthenticate.saudibusiness.gov.sa/certificate-details/0000135007"
                target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 font-bold mt-2"
                style={{ color: "var(--ol-primary)" }}
              >
                <ShieldCheck className="h-4 w-4" />
                {t.footerSBC}
              </a>
            </div>
          </div>
          <div className="border-t mt-10 pt-6 text-center text-xs text-[color:var(--ol-text-muted)]" style={{ borderColor: "var(--ol-border)" }}>
            {t.footerRights}
          </div>
        </div>
      </footer>

      {/* ===================== FLOATING WA (desktop) ===================== */}
      <a
        href={waLink()}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={t.waAria}
        className={`hidden md:flex fixed bottom-6 ${isRTL ? "left-6" : "right-6"} z-50 h-16 w-16 rounded-full items-center justify-center text-white shadow-2xl ol-pulse transition-transform hover:scale-110`}
        style={{ background: "var(--ol-wa)" }}
      >
        <WAIcon className="h-8 w-8" />
      </a>

      {/* ===================== MOBILE FIXED CTA BAR ===================== */}
      <div className="ol-mcta" role="region" aria-label="quick actions">
        <a href={`tel:${TEL}`} className="ol-btn ol-btn-outline">
          <Phone className="h-4 w-4" /> {t.mCtaCall}
        </a>
        <a href={waLink()} target="_blank" rel="noopener noreferrer" className="ol-btn ol-btn-wa">
          <WAIcon className="h-4 w-4" /> {t.waCta}
        </a>
      </div>
    </div>
  );
}
